import { useState, useCallback } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';

import { useTranslation } from '../i18n';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import { EntityWithId, Relationship } from '../types/types';

export interface DataToConfirmRemoving {
  relationship: Relationship;
  entity: EntityWithId;
  plugin: CommonReferencePickerPlugin;
}

interface UseRemoveEntityReturnType {
  dataToConfirmRemoving: DataToConfirmRemoving | null;
  setDataToConfirmRemoving: (data: DataToConfirmRemoving) => void;
  isRemoving: boolean;
  closeConfirmRemoveModal: () => void;
  handleRemoveConfirm: () => void;
}
/**
 * This hook handles remove entity process & return whether we need to show remove confirmation modal, show notification error etc.
 *
 * @param onRemove - a method returns a promise to handle relationship removal.
 * @returns {{dataToConfirmRemoving: *, closeConfirmRemoveModal: *, setDataToConfirmRemoving: *, isRemoving: *, handleRemoveConfirm: *}}
 */
export default (
  onRemove: (relationship: Relationship) => Promise<void | string[]>,
): UseRemoveEntityReturnType => {
  const { t } = useTranslation();
  const { showNotification } = useNotifications();
  const [isRemoving, setIsRemoving] = useState(false);
  const [dataToConfirmRemoving, setDataToConfirmRemovingState] =
    useState<DataToConfirmRemoving | null>(null);
  const closeConfirmRemoveModal = () => setDataToConfirmRemovingState(null);
  const projectId = useCurrentProjectId();
  const handleRemoveConfirm = () => {
    setIsRemoving(true);
    if (!dataToConfirmRemoving) return;
    onRemove(dataToConfirmRemoving.relationship)
      .then(() => {
        if (dataToConfirmRemoving.plugin.onRemove) {
          dataToConfirmRemoving.plugin.onRemove(
            dataToConfirmRemoving.relationship,
            dataToConfirmRemoving.entity,
            projectId,
          );
        }
        closeConfirmRemoveModal();
        setIsRemoving(false);
      })
      .catch((error) => {
        console.error(
          'ReferencePicker - useRemoveEntity: Error removing relationship',
          error,
        );
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: t('ERROR_REMOVING_RELATIONSHIPS_MESSAGE'),
        });
        setIsRemoving(false);
      });
  };

  const setDataToConfirmRemoving = useCallback(
    ({ relationship, entity, plugin }: DataToConfirmRemoving) => {
      setDataToConfirmRemovingState({
        relationship,
        entity,
        plugin,
      });
    },
    [],
  );

  return {
    dataToConfirmRemoving,
    setDataToConfirmRemoving,
    isRemoving,
    closeConfirmRemoveModal,
    handleRemoveConfirm,
  };
};
